import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Container from '@/components/Container';
import { Typography, Button } from '@mui/material';
import { Api } from '@/services/Api';
import { Site } from '@/declarations/models/Site';
import { ChildrenProp } from '@/declarations/ChildrenProp';
import { ToolbarHeader } from '@/components/ToolbarHeader';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import FullScreenModal from '@/components/FullScreenModal';
import { SiteEditor } from './AdminSiteEditor';
import { Status } from '../../declarations/models/Status';

export const AdminSites: FC<ChildrenProp> = () => {
  // console.log('selectedSite', selectedSite);
  const [sites, setSites] = useState<Array<Site>>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [siteId, setSiteId] = useState<number | null>(null);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const { t: tCommon } = useTranslation('common');

  const filteredSites = sites?.filter((site) => {
    const searchQueryLower = searchQuery.toLowerCase();
    return searchQueryLower.endsWith('*')
      ? site.name.toLowerCase().startsWith(searchQueryLower.slice(0, -1)) ||
          site?.name.toLowerCase().startsWith(searchQueryLower.slice(0, -1))
      : site.name.toLowerCase().includes(searchQueryLower) ||
          site?.owner?.name.toLowerCase().includes(searchQueryLower) ||
          site?.owner?.parent?.name?.toLowerCase().includes(searchQueryLower) ||
          site.owner_id?.toString().includes(searchQueryLower) ||
          (site?.id !== undefined && site?.id?.toString() === searchQueryLower);
  });

  const onAddNewSite = () => {
    // console.log('open owner modal');
    setSiteId(null);
    setEditModalOpen(true);
  };

  const handleOnItemClick = (item: Site) => {
    if (item.id) {
      setSiteId(item.id);
      setEditModalOpen(true);
    }
  };

  const [refreshDep, setRefreshDep] = useState(Date.now);
  const refreshPage = () => setRefreshDep(Date.now());

  const handleCloseModal = () => {
    refreshPage();
    setEditModalOpen(false);
  };

  useEffect(() => {
    Api.getAllSitesForSiteSharing(true)
      .fetch()
      .then((data) => {
        setSites(data[0] || []);
      });
  }, [refreshDep]);

  return (
    <Container left fullWidth p={2} top mt={0} column>
      <FullScreenModal modalOpen={editModalOpen}>
        <Container fullWidth left>
          <SiteEditor siteId={siteId} onCloseEditor={handleCloseModal} />
        </Container>
      </FullScreenModal>
      <ToolbarHeader
        heading={
          <Typography variant='h3' sx={{ component: 'h1', fontSize: 30 }}>
            Nettsteder ({sites?.length})
          </Typography>
        }
        topRight={
          <Button
            onClick={onAddNewSite}
            variant='contained'
            color='success'
            startIcon={<MaterialSymbol name='add' />}
            type='button'>
            Nytt nettsted
          </Button>
        }
        query={searchQuery}
        onQueryChange={setSearchQuery}
      />

      <Container left fullWidth pt={3} top mt={0} column sx={{ height: '75vh' }}>
        <TableContainer component={Paper}>
          <Table className='compact-table' stickyHeader sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>Nettsted ({filteredSites?.length})</TableCell>
                <TableCell align='center'>Nettsteds ID</TableCell>
                <TableCell align='center'>Status</TableCell>
                <TableCell>URL</TableCell>
                <TableCell align='center'>M24 versjon</TableCell>
                <TableCell>Eier</TableCell>
                <TableCell align='left'>Konsolidering</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSites
                ?.sort((a, b) => {
                  const aName = a.name.toLowerCase();
                  const bName = b.name.toLowerCase();
                  if (aName.startsWith('æ') || aName.startsWith('ø') || aName.startsWith('å')) {
                    return 1;
                  }
                  if (bName.startsWith('æ') || bName.startsWith('ø') || bName.startsWith('å')) {
                    return -1;
                  }

                  return aName.localeCompare(bName);
                })
                ?.map((site) => (
                  <TableRow key={site.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell scope='row'>
                      <Button
                        style={{ cursor: 'pointer', textDecoration: 'underline', textAlign: 'left' }}
                        onClick={() => handleOnItemClick(site)}>
                        {site.name}
                      </Button>
                    </TableCell>
                    <TableCell align='center'>{site.id}</TableCell>
                    <TableCell align='center'>{tCommon(`Status.${site.status}`)}</TableCell>
                    <TableCell>
                      {site.status === Status.PUBLISHED && site.url ? (
                        <a target='_blank' rel='noopener noreferrer' href={`//${site.url}`}>
                          {site.url}
                        </a>
                      ) : (
                        site.url || 'N/A'
                      )}
                    </TableCell>
                    <TableCell align='center'>{site.is_admin2_0_ready ? '2.0' : '1.0'}</TableCell>
                    <TableCell>{typeof site.owner === 'object' && site.owner ? site.owner.name : ''}</TableCell>
                    <TableCell align='left'>
                      {site.owner && site.owner.parent && site.owner.parent.name ? site.owner.parent.name : ''}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </Container>
  );
};

export default AdminSites;
